import React, { useEffect, useState } from "react";
import Modal from "ui/Modal";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "ui/Accordion";
import { 
  getReleaseNotes,
  getCurrentAppVersion, 
  getNoteTypeEmoji, 
  formatNoteType,
  ReleaseNote,
  VersionNotes
} from "config/releaseNotes";

interface ReleaseNotesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type NoteType = "feature" | "enhancement" | "fix" | "experimental";

// Function to group notes by type
const groupNotesByType = (notes: ReleaseNote[]) => {
  const grouped: Record<NoteType, ReleaseNote[]> = {
    feature: [],
    enhancement: [],
    fix: [],
    experimental: []
  };

  notes.forEach(note => {
    grouped[note.type].push(note);
  });

  return grouped;
};

const ReleaseNotesModal: React.FC<ReleaseNotesModalProps> = ({ isOpen, onClose }) => {
  // State for release notes and current version
  const [releaseNotes, setReleaseNotes] = useState<VersionNotes[]>([]);
  const [currentVersion, setCurrentVersion] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch both release notes and current version
        const [notes, version] = await Promise.all([
          getReleaseNotes(),
          getCurrentAppVersion()
        ]);
        
        setReleaseNotes(notes);
        setCurrentVersion(version);
      } catch (err) {
        console.error("Error fetching release notes data:", err);
        setError("Failed to load release notes. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchData();
    }
  }, [isOpen]);

  // Define the order of note types
  const typeOrder: NoteType[] = ["feature", "enhancement", "fix", "experimental"];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Release Notes"
    >
      <div className="flex flex-col h-full max-h-[90vh]">
        {/* Fixed header with intro text */}
        <div className="flex-shrink-0 pt-4 pb-2">
          <p className="text-eoe-text-sub dark:text-eoe-dark-text-sub mb-4">
            Thank you for trying the beta version of Societea. We're continuously improving the app and value your feedback.
          </p>
        </div>

        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto pr-2 pb-4">
          {loading && (
            <div className="flex justify-center py-8">
              <div className="text-eoe-text-sub dark:text-eoe-dark-text-sub">Loading release notes...</div>
            </div>
          )}

          {error && (
            <div className="text-red-500 dark:text-red-400 py-4 text-center">
              {error}
            </div>
          )}

          {!loading && !error && releaseNotes.length > 0 && (
            <Accordion type="single" collapsible defaultValue={releaseNotes[0]?.version}>
              {releaseNotes.map((versionData: VersionNotes) => (
                <AccordionItem key={versionData.version} value={versionData.version}>
                  <AccordionTrigger className="flex justify-between py-4 font-medium hover:no-underline">
                    <div className="flex items-center justify-between w-full pr-4">
                      <span className="text-sm font-semibold text-eoe-text dark:text-eoe-dark-text">
                        v{versionData.version}
                        {versionData.version === currentVersion && (
                          <span className="ml-2 text-xs bg-indigo-100 dark:bg-indigo-900 text-indigo-600 dark:text-indigo-400 px-2 py-0.5 rounded-full flex-shrink">
                            Current
                          </span>
                        )}
                      </span>
                      <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                        {versionData.date}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4 py-2">
                      {/* Group and display notes by type in specific order */}
                      {(() => {
                        const groupedNotes = groupNotesByType(versionData.notes);
                        
                        return typeOrder.map(type => {
                          if (groupedNotes[type] && groupedNotes[type].length > 0) {
                            return (
                              <div key={type} className="mb-3">
                                <div className="flex items-center font-semibold mb-2">
                                  <span className="mr-2">{getNoteTypeEmoji(type)}</span>
                                  <span>{formatNoteType(type)}</span>
                                </div>
                                <ul className="list-disc ml-8 space-y-1">
                                  {groupedNotes[type].map((note, idx) => (
                                    <li key={idx} className="text-eoe-text dark:text-eoe-dark-text">
                                      {note.description}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            );
                          }
                          return null;
                        });
                      })()}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}

          {!loading && !error && releaseNotes.length === 0 && (
            <div className="text-center py-8 text-eoe-text-sub dark:text-eoe-dark-text-sub">
              No release notes available at this time.
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ReleaseNotesModal; 